import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import PhoneInputIntl, { splitInternationalPhone } from "@/components/PhoneInputIntl";
import { RO_COUNTIES } from "@/lib/romaniaCounties";
import { ArrowRight, Loader2, MapPin, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1757439402296-000be181e38b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBleHRlcmlvciUyMG5pZ2h0fGVufDB8fHx8MTc3NzY5OTgwMXww&ixlib=rb-4.1.0&q=85";

export default function RegisterPage() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    client_type: "individual",
    vat_id: "",
    phone: "",
    contact_person: "",
    country_code: "RO",
    county_code: "",
    gdpr_data: false,
    gdpr_marketing: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // iter 64 — feedback 22.05: after register show "check inbox" instead of
  // auto-logging the user in. Backend now returns email_verification_required.
  const [pendingEmail, setPendingEmail] = useState("");
  // iter 81 (06.06.2026 production feedback): track whether the verification
  // email actually left our server. When false, the post-register screen
  // shows a clear error + inline Resend button instead of the misleading
  // "check your inbox" success state.
  const [emailSent, setEmailSent] = useState(true);
  const [emailError, setEmailError] = useState(null);
  const [resending, setResending] = useState(false);

  // Derive the active country from the phone the user just typed (e.g. picking
  // 🇲🇩 +373 in PhoneInputIntl sets country_code='MD' without an extra dropdown).
  // We update country_code whenever the phone changes so the country/county
  // section below always reflects the dialing prefix.
  const handlePhoneChange = (next) => {
    const { code } = splitInternationalPhone(next);
    setForm((f) => ({
      ...f,
      phone: next,
      country_code: code,
      // Clear county when leaving Romania — the field disappears, so its
      // value should not survive a re-selection of RO later.
      county_code: code === "RO" ? f.county_code : "",
    }));
  };

  // Sanitises name input — accepts Unicode letters (including ăâîșțĂÂÎȘȚ),
  // spaces, hyphens, apostrophes and dots. Rejects digits and other symbols.
  // For legal entities we additionally allow digits and common business
  // punctuation (e.g. "ACME 3D S.R.L.") since company names regularly contain
  // numerals. Feedback 22.05.2026: pure-number names were going through.
  const sanitiseName = (raw, clientType) => {
    if (!raw) return "";
    if (clientType === "legal") {
      // Legal: allow letters, digits, spaces, hyphens, dots, ampersands, commas
      return raw.replace(/[^\p{L}\p{N}\s.,&'\-]/gu, "");
    }
    // Individual: letters + spaces + hyphens + apostrophes + dots only
    return raw.replace(/[^\p{L}\s.'\-]/gu, "");
  };

  const isValidName = (raw, clientType) => {
    const v = (raw || "").trim();
    if (v.length < 2) return false;
    // Must contain at least 2 letters total (rejects "12", "--", "...")
    const letterCount = (v.match(/\p{L}/gu) || []).length;
    if (letterCount < 2) return false;
    if (clientType === "individual") {
      // Individuals can't have any digit
      if (/\d/.test(v)) return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidName(form.name, form.client_type)) {
      const msg =
        form.client_type === "legal"
          ? t("register.err_legal_name")
          : t("register.err_individual_name");
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!form.gdpr_data) {
      setError(t("register.err_gdpr_required"));
      return;
    }
    if (form.country_code === "RO" && !form.county_code) {
      setError(t("register.err_county_required"));
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error || t("register.err_register_failed"));
      return;
    }
    // iter 64: backend now returns either {email_verification_required: true}
    // OR the legacy {user, access_token} shape. AuthContext.register() forwards
    // the raw payload via res.payload.
    if (res.payload?.email_verification_required) {
      setPendingEmail(res.payload.email);
      // iter 81 (06.06.2026 production feedback): the backend now also tells
      // us whether the Brevo/SMTP send actually succeeded. We surface this
      // to the user immediately so they don't wait for an email that will
      // never arrive due to a transport error.
      setEmailSent(res.payload.email_sent !== false);
      setEmailError(res.payload.email_error || null);
      if (res.payload.email_sent === false) {
        toast.error(t("register.toast_email_failed"));
      } else {
        toast.success(t("register.toast_check_inbox"));
      }
      return;
    }
    toast.success(t("register.toast_success"));
    navigate("/dashboard", { replace: true });
  };

  // Inline resend handler — called from the "check inbox" / "send failed"
  // screen. Hits POST /api/auth/resend-verification, shows a clear success
  // or failure toast based on the actual transport result, and updates
  // local state so the screen flips from error to success on retry.
  const handleResend = async () => {
    if (!pendingEmail || resending) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email: pendingEmail });
      if (data.email_sent) {
        setEmailSent(true);
        setEmailError(null);
        toast.success(
          data.throttled
            ? t("register.toast_resend_throttled")
            : t("register.toast_resend_success"),
        );
      } else {
        setEmailSent(false);
        setEmailError(data.email_error || "unknown");
        toast.error(t("register.toast_resend_failed"));
      }
    } catch (e) {
      toast.error(t("register.toast_resend_failed"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background" data-testid="register-page">
      {/* Left visual */}
      <div className="relative hidden lg:block lg:col-span-2 overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Exterior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/55 to-black/90" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/">
            <Logo showText={false} />
          </Link>
          <div>
            <div className="font-cormorant italic text-zinc-300 text-3xl leading-tight max-w-md">
              {t("register.hero_quote")}
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mt-4">
              — One FDP
            </div>
          </div>
        </div>
      </div>

      {pendingEmail ? (
        // iter 64: post-register "check your inbox" screen.
        // iter 81: flips to an error variant when the verification email
        // failed to leave our server (Brevo/SMTP transport error). Both
        // variants share the same layout; only the icon, colors and
        // helper text differ.
        <div className="lg:col-span-3 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md text-center" data-testid="register-check-inbox">
            <div className="lg:hidden mb-8">
              <Link to="/">
                <Logo showText={false} />
              </Link>
            </div>
            <div
              className={`border rounded-xl p-8 ${
                emailSent
                  ? "border-orange-500/30 bg-zinc-900/40"
                  : "border-rose-500/40 bg-rose-500/5"
              }`}
              data-testid={emailSent ? "register-email-sent" : "register-email-failed"}
            >
              <div
                className={`w-20 h-20 mx-auto rounded-full border-2 flex items-center justify-center mb-4 ${
                  emailSent
                    ? "bg-orange-500/15 border-orange-500/40"
                    : "bg-rose-500/15 border-rose-500/40"
                }`}
              >
                {emailSent ? (
                  <CheckCircle2 size={36} className="text-orange-400" strokeWidth={1.8} />
                ) : (
                  <AlertTriangle size={36} className="text-rose-400" strokeWidth={1.8} />
                )}
              </div>
              <h1 className="font-outfit font-black text-white text-2xl mb-3">
                {emailSent
                  ? t("auth.verify_title")
                  : t("register.verify_failed_title")}
              </h1>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                {emailSent ? (
                  <>
                    {t("auth.verify_subtitle", { email: pendingEmail })}
                    <br />
                    <br />
                    {t("auth.verify_check_spam")}
                  </>
                ) : (
                  <>
                    {t("register.verify_failed_intro", { email: pendingEmail })}
                    <br />
                    <br />
                    <span className="text-rose-300/80">
                      {t("register.verify_failed_hint")}
                    </span>
                  </>
                )}
              </p>
              <div className="space-y-3">
                <Button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className={`w-full ${
                    emailSent
                      ? "bg-orange-500 hover:bg-orange-600 text-white"
                      : "bg-rose-500 hover:bg-rose-600 text-white"
                  }`}
                  data-testid="register-resend-btn"
                >
                  {resending ? (
                    <>
                      <Loader2 size={16} className="animate-spin mr-2" />
                      {t("register.resend_loading")}
                    </>
                  ) : emailSent ? (
                    t("register.resend_again")
                  ) : (
                    t("register.resend_now")
                  )}
                </Button>
                <Link
                  to="/login"
                  className="block text-xs text-zinc-500 hover:text-zinc-300"
                  data-testid="register-back-to-login"
                >
                  {t("auth.login_button")}
                </Link>
                {!emailSent && (
                  <div
                    className="text-[11px] text-zinc-500 mt-3 px-3 py-2 rounded bg-zinc-900/40 border border-white/5"
                    data-testid="register-support-hint"
                  >
                    {t("register.verify_failed_support")}{" "}
                    <a
                      href="mailto:lead@fabricadeprofile.ro"
                      className="text-orange-400 hover:text-orange-300 underline"
                    >
                      lead@fabricadeprofile.ro
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
      /* Right form */
      <div className="lg:col-span-3 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-xl">
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden">
              <Link to="/">
                <Logo showText={false} />
              </Link>
            </div>
            <LanguageToggle className="ml-auto" />
          </div>

          <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3">
            {t("auth.register_title")}
          </div>
          <h1 className="font-outfit font-black text-white text-4xl tracking-tighter leading-tight mb-2">
            {t("auth.register_title")}
          </h1>
          <p className="text-zinc-400 mb-8">
            {t("auth.register_subtitle")}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                {t("register.client_type_label")}
              </Label>
              <RadioGroup
                value={form.client_type}
                onValueChange={(v) => setForm({ ...form, client_type: v })}
                className="grid grid-cols-2 gap-3 mt-2"
              >
                <label
                  htmlFor="t-individual"
                  className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition select-none ${
                    form.client_type === "individual"
                      ? "border-orange-500 bg-orange-500/5"
                      : "border-white/10 bg-zinc-900/40 hover:border-white/20"
                  }`}
                  data-testid="register-type-individual"
                >
                  <RadioGroupItem
                    value="individual"
                    id="t-individual"
                    className="border-white/40"
                  />
                  <span className="text-white">{t("register.type_individual")}</span>
                </label>
                <label
                  htmlFor="t-legal"
                  className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition select-none ${
                    form.client_type === "legal"
                      ? "border-orange-500 bg-orange-500/5"
                      : "border-white/10 bg-zinc-900/40 hover:border-white/20"
                  }`}
                  data-testid="register-type-legal"
                >
                  <RadioGroupItem value="legal" id="t-legal" className="border-white/40" />
                  <span className="text-white">{t("register.type_legal")}</span>
                </label>
              </RadioGroup>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                  {form.client_type === "legal" ? t("register.name_label_legal") : t("register.name_label_individual")}
                </Label>
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: sanitiseName(e.target.value, form.client_type) })
                  }
                  className="bg-zinc-900/50 border-white/10 text-white mt-2 h-12"
                  data-testid="register-name-input"
                  pattern={form.client_type === "individual" ? "^[\\p{L}\\s.'\\-]+$" : undefined}
                  title={
                    form.client_type === "individual"
                      ? t("register.name_pattern_title_individual")
                      : t("register.name_pattern_title_legal")
                  }
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                  {t("register.email_label")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-zinc-900/50 border-white/10 text-white mt-2 h-12"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label
                  htmlFor="password"
                  className="text-zinc-300 text-xs uppercase tracking-[0.2em]"
                >
                  {t("register.password_label")}
                </Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-zinc-900/50 border-white/10 text-white mt-2 h-12"
                  data-testid="register-password-input"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                  {t("register.phone_label")}
                </Label>
                <div className="mt-2">
                  <PhoneInputIntl
                    value={form.phone}
                    onChange={handlePhoneChange}
                    placeholder={t("register.phone_placeholder")}
                    testid="register-phone"
                    inputClassName="h-12"
                  />
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">
                  {t("register.phone_hint")}
                </p>
              </div>

              {/* Country + county block (iter 63) */}
              {form.country_code === "RO" ? (
                <div data-testid="register-county-block">
                  <Label className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                    {t("register.county_label")} <span className="text-orange-400">{t("register.county_required")}</span>
                  </Label>
                  <select
                    required
                    value={form.county_code}
                    onChange={(e) => setForm({ ...form, county_code: e.target.value })}
                    className="w-full mt-2 h-12 rounded-md bg-zinc-900/50 border border-white/10 text-white text-sm px-3 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                    data-testid="register-county"
                  >
                    <option value="">{t("register.county_placeholder")}</option>
                    {RO_COUNTIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-zinc-500 mt-1">
                    {t("register.county_hint")}
                  </p>
                </div>
              ) : (
                <div data-testid="register-country-block">
                  <Label className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                    {t("register.country_origin_label")}
                  </Label>
                  <div
                    className="mt-2 h-12 rounded-md bg-zinc-900/30 border border-white/10 text-white text-sm px-3 flex items-center gap-2"
                    data-testid="register-country-readonly"
                  >
                    <MapPin size={14} className="text-orange-400" />
                    <span className="font-medium">
                      {{
                        MD: "🇲🇩 Republica Moldova",
                        HU: "🇭🇺 Ungaria",
                        BG: "🇧🇬 Bulgaria",
                        AT: "🇦🇹 Austria",
                        DE: "🇩🇪 Germania",
                        FR: "🇫🇷 Franța",
                        IT: "🇮🇹 Italia",
                        ES: "🇪🇸 Spania",
                        PT: "🇵🇹 Portugalia",
                        GB: "🇬🇧 Marea Britanie",
                        IE: "🇮🇪 Irlanda",
                        NL: "🇳🇱 Olanda",
                        BE: "🇧🇪 Belgia",
                        CH: "🇨🇭 Elveția",
                        PL: "🇵🇱 Polonia",
                        CZ: "🇨🇿 Cehia",
                        SK: "🇸🇰 Slovacia",
                        GR: "🇬🇷 Grecia",
                        TR: "🇹🇷 Turcia",
                        US: "🇺🇸 Statele Unite",
                        CA: "🇨🇦 Canada",
                      }[form.country_code] || form.country_code}
                    </span>
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">
                      {t("register.auto_detected")}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {form.client_type === "legal" && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="vat_id"
                    className="text-zinc-300 text-xs uppercase tracking-[0.2em]"
                  >
                    {t("register.vat_label")}
                  </Label>
                  <Input
                    id="vat_id"
                    value={form.vat_id}
                    onChange={(e) => setForm({ ...form, vat_id: e.target.value })}
                    className="bg-zinc-900/50 border-white/10 text-white mt-2 h-12"
                    data-testid="register-vat-input"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="contact_person"
                    className="text-zinc-300 text-xs uppercase tracking-[0.2em]"
                  >
                    {t("register.contact_person_label")}
                  </Label>
                  <Input
                    id="contact_person"
                    value={form.contact_person}
                    onChange={(e) =>
                      setForm({ ...form, contact_person: sanitiseName(e.target.value, "individual") })
                    }
                    pattern="^[\p{L}\s.'\-]*$"
                    title={t("register.contact_person_title")}
                    className="bg-zinc-900/50 border-white/10 text-white mt-2 h-12"
                    data-testid="register-contact-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={form.gdpr_data}
                  onCheckedChange={(v) => setForm({ ...form, gdpr_data: !!v })}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                  data-testid="register-gdpr-data"
                />
                <span className="text-sm text-zinc-300 leading-relaxed">
                  {t("register.gdpr_data_text_1")}{" "}
                  <strong className="text-zinc-100">WEASEL ART SRL</strong>{" "}
                  {t("register.gdpr_data_text_2")}{" "}
                  <a
                    href="/politica-confidentialitate"
                    target="_blank"
                    rel="noreferrer"
                    className="text-orange-400 hover:text-orange-300 underline"
                    data-testid="register-privacy-link"
                  >
                    {t("register.gdpr_data_link")}
                  </a>
                  .<span className="text-orange-500"> *</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={form.gdpr_marketing}
                  onCheckedChange={(v) => setForm({ ...form, gdpr_marketing: !!v })}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                  data-testid="register-gdpr-marketing"
                />
                <span className="text-sm text-zinc-400 leading-relaxed">
                  {t("register.gdpr_marketing_text")}
                </span>
              </label>
            </div>

            {error && (
              <div
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 px-4 py-3 rounded-md"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white btn-orange-glow"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  {t("register.submit_btn")}
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-sm text-zinc-400">
            {t("register.have_account")}{" "}
            <Link
              to="/login"
              className="text-orange-500 hover:text-orange-400 transition"
              data-testid="register-to-login-link"
            >
              {t("register.login_link")}
            </Link>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
